/**
 * The consonance table of strict two-voice counterpoint.
 *
 * Both the diatonic distance and the EDO-step content matter: a written fifth
 * must have the tuning's perfect-fifth size, and the number alone would let a
 * diminished or augmented interval through.
 */

/**
 * Is the interval consonant between two voices?
 *
 * @param dd   diatonic distance, upper minus lower, ≥ 0
 * @param steps the same distance in the tuning's EDO steps
 * @param edo   equal divisions of the octave
 *
 * Consonances are unisons/octaves, thirds, perfect fifths and sixths. The
 * perfect fourth is a dissonance here — the classic trap of the style.
 */
export function isConsonant(dd: number, steps: number, edo: 12 | 31 = 12): boolean {
  const cls = ((dd % 7) + 7) % 7;
  const s = ((steps % edo) + edo) % edo;
  const sizes = edo === 31
    ? { minorThird: 8, majorThird: 10, fifth: 18, minorSixth: 21, majorSixth: 23 }
    : { minorThird: 3, majorThird: 4, fifth: 7, minorSixth: 8, majorSixth: 9 };
  switch (cls) {
    case 0: return s === 0;
    case 2: return s === sizes.minorThird || s === sizes.majorThird;
    case 4: return s === sizes.fifth;
    case 5: return s === sizes.minorSixth || s === sizes.majorSixth;
    default: return false;
  }
}

/** Perfect consonances: the unison/octave and the perfect fifth. */
export function isPerfectConsonance(dd: number, steps: number, edo: 12 | 31 = 12): boolean {
  const cls = ((dd % 7) + 7) % 7;
  const s = ((steps % edo) + edo) % edo;
  return (cls === 0 && s === 0) || (cls === 4 && s === (edo === 31 ? 18 : 7));
}

/**
 * The interval number reduced to its simple class: a 10th reads as 3, a 12th as
 * 5, any octave multiple as 8. This is the number shown on the interval rail.
 */
export const simpleNumber = (dd: number): number =>
  dd === 0 ? 1 : dd % 7 === 0 ? 8 : (dd % 7) + 1;

/** Human name for a dissonance, used in finding messages. */
export function dissonanceName(dd: number, steps: number, edo: 12 | 31 = 12): string {
  const cls = ((dd % 7) + 7) % 7;
  const s = ((steps % edo) + edo) % edo;
  if (cls === 3 && s === (edo === 31 ? 13 : 5)) return 'perfect 4th';
  if (edo === 31 ? s === 15 || s === 16 : s === 6) return 'tritone';
  return `dissonant ${simpleNumber(dd)}${suffix(simpleNumber(dd))}`;
}

/** Whether a tuning-step distance is either notated form of a tritone. */
export function isTritone(steps: number, edo: 12 | 31 = 12): boolean {
  const s = ((steps % edo) + edo) % edo;
  return edo === 31 ? s === 15 || s === 16 : s === 6;
}

const suffix = (n: number): string => {
  const teens = n % 100;
  if (teens >= 11 && teens <= 13) return 'th';
  return ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
};
